/**
 * Modern Cart Hook - Connects to UnifiedCartManager Service
 * 
 * This hook provides the same interface as the legacy useCart() hook
 * but connects to our new UnifiedCartManager with polymorphic references.
 * 
 * Features:
 * - Zero data duplication through polymorphic references
 * - Full DiscountEngine integration (SAVE20 coupon system)
 * - Full AddonServicesService integration (645-line enterprise system)
 * - Multi-currency support with precision handling
 * - Real-time calculations and validation
 * - Optimistic updates with rollback on error
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { unifiedCartManager, type CartTotals, type CartItemInput, type AddonServiceSelection, type DiscountContext } from '@/services/cart/UnifiedCartManager';
import { modernCartService } from '@/services/ModernCartService';
import { realProfileService } from '@/services/RealProfileService';
import { logger } from '@/utils/logger';
import { toast } from '@/hooks/use-toast';

export interface ModernCartItem {
  id: string;
  sourceType: 'quote' | 'product' | 'consolidation';
  sourceId: string;
  quantity: number;
  currency: string;
  originalAmount: number;
  discountedAmount?: number;
  addonCost?: number;
  selectedAddons: AddonServiceSelection[];
  appliedDiscounts: any[];
  options?: Record<string, any>; // 🔥 NEW: Product variant options (size, color, etc.)
  variantId?: string; // 🔥 NEW: Catalog variant ID
  // For compatibility with existing components
  quote: any; // Will be resolved from polymorphic reference
  // NEW PHASE 3: Rich display metadata for proper cart display
  displayMetadata?: {
    displayName: string;
    productUrl: string;
    productImage: string | null;
    brand: string | null;
    sourceType: string;
    routeInfo?: {
      originCountry?: string;
      destCountry?: string;
      marketplace?: string;
    };
  };
}

export interface ModernCartState {
  items: ModernCartItem[];
  isLoading: boolean;
  isInitialized: boolean;
  syncStatus: 'synced' | 'syncing' | 'error' | 'offline';
  error: string | null;
  totals: CartTotals | null;
}

export interface ModernCartActions {
  addItem: (quote: any) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  applyDiscount: (code: string, context: DiscountContext) => Promise<void>;
  removeDiscount: (discountId: string) => Promise<void>;
  applyAddonService: (serviceKey: string, amount: number) => Promise<void>;
  removeAddonService: (serviceKey: string) => Promise<void>;
  getRecommendedAddons: (context: { country: string; customerTier?: string }) => Promise<any>;
  refresh: () => Promise<void>;
}

export function useModernCart(): ModernCartState & ModernCartActions {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [cartId, setCartId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize cart session
  useEffect(() => {
    const initializeCart = async () => {
      try {
        // Get or create user ID (for guest users, use persistent guest ID from localStorage)
        let userId: string;
        if (user?.id) {
          userId = user.id;
        } else {
          // Create or get persistent guest ID
          const existingGuestId = localStorage.getItem('iWishBag_guestId');
          if (existingGuestId) {
            userId = existingGuestId;
          } else {
            userId = `guest-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('iWishBag_guestId', userId);
          }
        }

        // DISABLED: Logger causing infinite re-render loop
        // logger.info('[MODERN-CART] Initializing cart for user:', userId);

        // Check if user already has a valid cart session (not expired) - using ModernCartService
        const existingSessions = await modernCartService.getCartSessions(userId);

        let sessionId: string;
        if (existingSessions && existingSessions.length > 0) {
          // Check each session for items to prefer sessions with content
          let sessionWithItems = null;
          for (const session of existingSessions) {
            const items = await modernCartService.getCartItems(session.id);

            if (items && items.length > 0) {
              sessionWithItems = session;
              break;
            }
          }

          const selectedSession = sessionWithItems || existingSessions[0];
          sessionId = selectedSession.id;
          // DISABLED: Logger causing infinite re-render loop
          // logger.info('[MODERN-CART] Using existing cart session:', sessionId,
          //   sessionWithItems ? '(has items)' : '(most recent)');
        } else {
          const session = await modernCartService.createCartSession(userId);
          sessionId = session.id;
          // DISABLED: Logger causing infinite re-render loop
          // logger.info('[MODERN-CART] Created new cart session:', sessionId);
        }

        setCartId(sessionId);
      } catch (error) {
        logger.error('[MODERN-CART] Failed to initialize cart:', error);
        setError(error instanceof Error ? error.message : 'Failed to initialize cart');
      }
    };

    initializeCart();
  }, [user]);

  // Query cart items (ENHANCED: Phase 3 - uses enriched cart items with display metadata)
  const { 
    data: cartData, 
    isLoading, 
    error: queryError,
    refetch 
  } = useQuery({
    queryKey: ['modern-cart', cartId],
    queryFn: async (): Promise<{ items: ModernCartItem[]; totals: CartTotals }> => {
      console.log('[cart-debug] useModernCart - Starting query function:', {
        cartId,
        timestamp: new Date().toISOString()
      });

      if (!cartId) {
        const emptyResult = { items: [], totals: { subtotal: 0, discountAmount: 0, addonAmount: 0, taxAmount: 0, totalAmount: 0, currency: 'USD', appliedDiscounts: [], appliedAddons: [] } };
        console.log('[cart-debug] useModernCart - No cartId, returning empty:', emptyResult);
        return emptyResult;
      }

      // 🔥 FIX: Get user's display currency for quote conversions - using ModernCartService
      const cartSession = await modernCartService.getCartSession(cartId);
      let displayCurrency = 'USD'; // Default fallback

      if (cartSession?.user_id) {
        const userProfile = await realProfileService.getProfile(cartSession.user_id);
        displayCurrency = userProfile?.preferred_display_currency || 'USD';
      }
      
      console.log('[quote-item-cart] useModernCart - Display currency determined:', {
        cartId,
        userId: cartSession?.user_id,
        displayCurrency,
        debugNote: 'Will use this for quote currency conversions'
      });

      // 🔥 FIX: Reduced logging for performance
      const enrichedCartItems = await unifiedCartManager.getEnrichedCartItems(cartId);

      const items: ModernCartItem[] = [];

      // Transform enriched items to ModernCartItem format
      for (const enrichedItem of enrichedCartItems) {
        try {
          // Get the original source data for compatibility
          let resolvedData: any = null;
          switch (enrichedItem.source_type) {
            case 'quote': {
              const { data: quote } = await supabase
                .from('quotes_v2')
                .select('*')
                .eq('id', enrichedItem.source_id)
                .single();
              resolvedData = quote;
              break;
            }
            
            case 'product': {
              // Use AWS catalog service for product data
              const { awsCatalogService } = await import('@/services/AWSCatalogService');
              const result = await awsCatalogService.getProducts({}, { limit: 200 });

              const product = result.success && result.data
                ? result.data.find(p => p.id === enrichedItem.source_id)
                : null;

              resolvedData = product;
              break;
            }

            case 'consolidation': {
              const { data: consolidation } = await supabase
                .from('consolidation_requests')
                .select('*')
                .eq('id', enrichedItem.source_id)
                .single();
              resolvedData = consolidation;
              break;
            }
          }

          if (resolvedData) {
            // 🔥 COMPREHENSIVE CURRENCY FIX: Handle all item types with proper currency conversion
            let itemOriginalAmount = enrichedItem.original_amount || 0;
            let itemCurrency = enrichedItem.currency || 'USD';
            let sourceCurrency = itemCurrency;
            let sourceAmount = itemOriginalAmount;
            
            // CLEAN: Direct calculation approach - same as quote pages fix
            switch (enrichedItem.source_type) {
              case 'quote':
                // SIMPLE: Use direct calculation data without complex currency conversion
                if (resolvedData.calculation_data?.data?.totals?.total) {
                  sourceAmount = resolvedData.calculation_data.data.totals.total;
                  sourceCurrency = 'INR'; // Origin country currency - direct, simple approach
                  console.log('🔍 [CART-TOTAL-DIRECT] Quote using direct calculation approach (like quote pages):', {
                    itemId: enrichedItem.id,
                    directAmount: sourceAmount,
                    currency: sourceCurrency,
                    approach: 'direct_calculation_data_no_complex_conversion',
                    timestamp: new Date().toISOString()
                  });
                }
                break;
                
              case 'product':
                if (resolvedData.price_origin && resolvedData.origin_currency) {
                  sourceAmount = resolvedData.price_origin;
                  sourceCurrency = resolvedData.origin_currency;
                  console.log('[currency-conversion] Product data extracted:', {
                    itemId: enrichedItem.id,
                    sourceAmount,
                    sourceCurrency,
                    dataSource: 'catalog_products.price_origin'
                  });
                }
                break;
                
              case 'consolidation':
                // Consolidation items might have different data structure
                if (resolvedData.consolidation_data?.total_cost && resolvedData.consolidation_data?.currency) {
                  sourceAmount = resolvedData.consolidation_data.total_cost;
                  sourceCurrency = resolvedData.consolidation_data.currency;
                  console.log('[currency-conversion] Consolidation data extracted:', {
                    itemId: enrichedItem.id,
                    sourceAmount,
                    sourceCurrency,
                    dataSource: 'consolidation_requests.consolidation_data'
                  });
                } else if (enrichedItem.original_amount && enrichedItem.original_amount > 0) {
                  sourceAmount = enrichedItem.original_amount;
                  sourceCurrency = enrichedItem.currency || 'USD';
                  console.log('[currency-conversion] Consolidation using stored amount:', {
                    itemId: enrichedItem.id,
                    sourceAmount,
                    sourceCurrency,
                    dataSource: 'cart_items.original_amount'
                  });
                }
                break;
            }
            
            // SIMPLIFIED: Direct approach for quotes, preserve complex logic for products/consolidation
            if (enrichedItem.source_type === 'quote') {
              // DIRECT: For quotes, use source amount directly (same as quote pages fix)
              itemOriginalAmount = sourceAmount;
              itemCurrency = sourceCurrency;
              
              console.log('[CART-QUOTE-DIRECT] Using direct approach for quote (no complex conversion):', {
                itemId: enrichedItem.id,
                directAmount: sourceAmount,
                currency: sourceCurrency,
                approach: 'same_as_quote_pages_fix',
                reasoning: 'calculation_data.total already in correct format'
              });
            } else {
              // PRESERVE: Complex conversion logic for products and consolidation items
              if (sourceCurrency && sourceCurrency !== displayCurrency && sourceAmount > 0) {
                try {
                  const { convertAmountWithSessionLock } = await import('@/services/SessionCurrencyService');
                  const convertedAmount = await convertAmountWithSessionLock(sourceAmount, sourceCurrency, displayCurrency);
                  
                  itemOriginalAmount = convertedAmount;
                  itemCurrency = displayCurrency;
                  
                  console.log('[currency-conversion] Conversion applied (non-quote item):', {
                    itemType: enrichedItem.source_type,
                    itemId: enrichedItem.id,
                    sourceId: enrichedItem.source_id,
                    originalAmount: sourceAmount,
                    originalCurrency: sourceCurrency,
                    convertedAmount,
                    displayCurrency,
                    conversionRate: (convertedAmount / sourceAmount).toFixed(4)
                  });
                } catch (conversionError) {
                  console.error('[currency-conversion] Conversion failed:', {
                    itemType: enrichedItem.source_type,
                    itemId: enrichedItem.id,
                    error: conversionError,
                    fallbackAmount: sourceAmount,
                    fallbackCurrency: sourceCurrency
                  });
                  itemOriginalAmount = sourceAmount;
                  itemCurrency = sourceCurrency;
                }
              } else {
                itemOriginalAmount = sourceAmount;
                itemCurrency = sourceCurrency;
                
                console.log('[currency-conversion] No conversion needed (non-quote item):', {
                  itemType: enrichedItem.source_type,
                  itemId: enrichedItem.id,
                  amount: sourceAmount,
                  currency: sourceCurrency,
                  displayCurrency,
                  reason: sourceCurrency === displayCurrency ? 'same_currency' : sourceAmount <= 0 ? 'zero_amount' : 'no_currency_info'
                });
              }
            }
            
            const modernItem: ModernCartItem = {
              id: enrichedItem.id,
              sourceType: enrichedItem.source_type as any,
              sourceId: enrichedItem.source_id!,
              quantity: enrichedItem.quantity || 1,
              currency: itemCurrency,
              originalAmount: itemOriginalAmount,
              discountedAmount: enrichedItem.discounted_amount || itemOriginalAmount,
              addonCost: enrichedItem.addon_cost || 0,
              selectedAddons: (enrichedItem.selected_addon_services as AddonServiceSelection[]) || [],
              appliedDiscounts: (enrichedItem.applied_discounts as any[]) || [],
              options: enrichedItem.selected_options || {}, // 🔥 CRITICAL: Map database field to interface
              variantId: enrichedItem.catalog_variant_id || undefined, // 🔥 CRITICAL: Map variant ID
              quote: resolvedData, // For compatibility with existing components
              // NEW PHASE 3: Add enriched display metadata
              displayMetadata: enrichedItem.displayMetadata
            };
            
            // Debug variant mapping
            console.log('[cart-variants] DEBUG useModernCart - Mapping item with variants:', {
              itemId: enrichedItem.id,
              sourceType: enrichedItem.source_type,
              selected_options: enrichedItem.selected_options,
              catalog_variant_id: enrichedItem.catalog_variant_id,
              mappedOptions: modernItem.options,
              mappedVariantId: modernItem.variantId,
              hasOptions: !!modernItem.options && Object.keys(modernItem.options).length > 0
            });
            
            items.push(modernItem);
          }
        } catch (itemError) {
          logger.error('[MODERN-CART-ENHANCED] Failed to resolve enriched item:', itemError);
        }
      }

      // 🔥 DEBUG: Track input to calculateTotals for quote items
      const quoteItemsPreCalculation = items.filter(item => item.sourceType === 'quote');
      if (quoteItemsPreCalculation.length > 0) {
        console.log('[quote-item-cart] useModernCart - About to call calculateTotals with quote items:', {
          cartId,
          quoteItemCount: quoteItemsPreCalculation.length,
          quoteItemInputData: quoteItemsPreCalculation.map(item => ({
            id: item.id,
            sourceId: item.sourceId,
            originalAmount: item.originalAmount,
            currency: item.currency,
            quantity: item.quantity,
            quoteCalculationData: item.quote?.calculation_data,
            quoteTotalOrigin: item.quote?.total_quote_origincurrency,
            quoteCalculationTotal: item.quote?.calculation_data?.data?.totals?.total,
            ISSUE_CHECK: {
              correctTotal: item.quote?.calculation_data?.data?.totals?.total,
              cartStoredAmount: item.originalAmount,
              problemDetected: (item.quote?.calculation_data?.data?.totals?.total || 0) !== (item.originalAmount || 0)
            }
          })),
          timestamp: new Date().toISOString(),
          debugNote: 'Input data being sent to UnifiedCartManager.calculateTotals()'
        });
      }

      // Calculate totals
      const totals = await unifiedCartManager.calculateTotals(cartId);

      // 🔥 DEBUG: Track quote item calculations specifically
      const quoteItems = items.filter(item => item.sourceType === 'quote');
      if (quoteItems.length > 0) {
        console.log('🔍 [CART-TOTAL-DEBUG] useModernCart - Quote item analysis after calculateTotals:', {
          cartId,
          quoteItemCount: quoteItems.length,
          totalItemCount: items.length,
          calculatedTotals: totals,
          CRITICAL_ANALYSIS: {
            expectedTotalFromQuoteData: quoteItems.reduce((sum, item) => sum + (item.quote?.calculation_data?.data?.totals?.total || 0), 0),
            calculatedSubtotal: totals.subtotal,
            discrepancy: Math.abs((quoteItems.reduce((sum, item) => sum + (item.quote?.calculation_data?.data?.totals?.total || 0), 0)) - (totals.subtotal || 0)),
            issue: 'If discrepancy > 0, cart is using wrong pricing source'
          },
          quoteItemDetails: quoteItems.map(item => ({
            id: item.id,
            sourceId: item.sourceId,
            quantity: item.quantity,
            originalAmount: item.originalAmount,
            discountedAmount: item.discountedAmount,
            currency: item.currency,
            hasQuoteData: !!item.quote,
            quoteCalculationData: item.quote?.calculation_data ? 'present' : 'missing',
            quoteTotalOrigin: item.quote?.total_quote_origincurrency,
            quoteCalculationTotal: item.quote?.calculation_data?.data?.totals?.total,
            expectedIndividualTotal: (item.originalAmount || 0) * (item.quantity || 1)
          })),
          calculatedSubtotal: totals.subtotal,
          calculatedTotalAmount: totals.totalAmount,
          timestamp: new Date().toISOString(),
          debugNote: 'Analyzing why quote totals might show as 0 in cart summary'
        });

        // Calculate expected quote contribution to totals
        let expectedQuotesTotalContribution = 0;
        quoteItems.forEach(item => {
          expectedQuotesTotalContribution += (item.originalAmount || 0) * (item.quantity || 1);
        });

        console.log('[quote-item-cart] useModernCart - Quote total contribution analysis:', {
          cartId,
          expectedQuotesTotalContribution,
          calculatedSubtotal: totals.subtotal,
          quotesVsCalculatedDifference: Math.abs(expectedQuotesTotalContribution - totals.subtotal),
          isZeroSubtotalIssue: totals.subtotal === 0 && expectedQuotesTotalContribution > 0,
          possibleIssue: totals.subtotal === 0 && expectedQuotesTotalContribution > 0 ? 'Zero totals despite non-zero quote amounts - potential calculation error' : 'Totals appear consistent',
          timestamp: new Date().toISOString()
        });
      }

      console.log('[cart-debug] useModernCart - Query results ready:', {
        cartId,
        itemCount: items.length,
        itemIds: items.map(item => item.id),
        totals,
        itemsSummary: items.map(item => ({
          id: item.id,
          sourceType: item.sourceType,
          originalAmount: item.originalAmount,
          quantity: item.quantity,
          currency: item.currency
        })),
        timestamp: new Date().toISOString()
      });

      // Calculate expected sum from individual items for comparison
      let expectedItemsSum = 0;
      for (const item of items) {
        expectedItemsSum += (item.originalAmount || 0) * (item.quantity || 1);
      }

      console.log('[cart-debug] useModernCart - DISCREPANCY CHECK:', {
        expectedItemsSum: expectedItemsSum,
        calculatedSubtotal: totals.subtotal,
        difference: Math.abs(expectedItemsSum - totals.subtotal),
        percentDifference: expectedItemsSum > 0 ? ((Math.abs(expectedItemsSum - totals.subtotal) / expectedItemsSum) * 100).toFixed(2) + '%' : '0%',
        note: 'This is a rough comparison - currency conversion and precision may cause legitimate differences',
        timestamp: new Date().toISOString()
      });
      
      return { items, totals };
    },
    enabled: !!cartId,
    staleTime: 30 * 1000, // 🔥 Optimized: 30 seconds for balance between performance and freshness
    refetchOnWindowFocus: true, // 🔥 Enable refetch on focus for latest data
    refetchOnMount: true, // 🔥 Enable refetch on mount for consistency
  });

  // Add item mutation with optimistic updates
  const addItemMutation = useMutation({
    mutationFn: async (quote: any) => {
      if (!cartId) throw new Error('Cart not initialized');
      
      // ENHANCEMENT: Detect source type based on item structure
      let sourceType: 'quote' | 'product' | 'consolidation' = 'quote';
      
      // Check if this is a consolidation item (from MyConsolidations)
      if (quote.customer_data?.service_type === 'consolidation' || 
          quote.customer_data?.consolidation_request_id) {
        sourceType = 'consolidation';
        console.log('[MODERN-CART] Detected consolidation item for polymorphic cart');
      }
      // Check if this is a catalog product (would have different structure)
      else if (quote.product_id || quote.catalog_id) {
        sourceType = 'product';
        console.log('[MODERN-CART] Detected product item for polymorphic cart');
      }
      // Default to quote
      else {
        console.log('[MODERN-CART] Using default quote source type for polymorphic cart');
      }
      
      const cartItemInput: CartItemInput = {
        sourceType,
        sourceId: quote.id,
        quantity: quote.customer_data?.quantity || 1, // Use quantity from product data
        selectedAddonServices: []
      };

      await unifiedCartManager.addItem(cartId, cartItemInput);
    },
    // 🔥 OPTIMISTIC UPDATES - Update UI immediately before server call
    onMutate: async (quote) => {
      if (!cartId) return;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['modern-cart', cartId] });
      
      // Snapshot the previous value for rollback
      const previousCart = queryClient.getQueryData(['modern-cart', cartId]);
      
      // Optimistically update the cart count
      queryClient.setQueryData(['modern-cart', cartId], (old: any) => {
        if (!old) return old;
        
        // Create optimistic cart item
        const optimisticItem: ModernCartItem = {
          id: `temp-${Date.now()}`, // Temporary ID
          sourceType: quote.product_id ? 'product' : 'quote',
          sourceId: quote.id,
          quantity: 1,
          currency: quote.currency || 'USD',
          originalAmount: quote.calculation_data?.data?.totals?.total || 0, // Clean direct approach like other components
          selectedAddons: [],
          appliedDiscounts: [],
          quote: quote
        };
        
        return {
          ...old,
          items: [...old.items, optimisticItem]
        };
      });
      
      return { previousCart };
    },
    onSuccess: () => {
      // Invalidate and refetch to get accurate server data
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
      // Success toast removed - user can see item in cart state change
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousCart && cartId) {
        queryClient.setQueryData(['modern-cart', cartId], context.previousCart);
      }
      
      logger.error('[MODERN-CART] Add item failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add item to cart',
        variant: 'destructive',
      });
    }
  });

  // Remove item mutation with optimistic updates
  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (!cartId) {
        throw new Error('Cart not initialized - cannot remove item');
      }
      
      await unifiedCartManager.removeItem(cartId, itemId);
    },
    // 🔥 OPTIMISTIC UPDATES - Update UI immediately before server call
    onMutate: async (itemId) => {
      if (!cartId) return;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['modern-cart', cartId] });
      
      // Snapshot the previous value for rollback
      const previousCart = queryClient.getQueryData(['modern-cart', cartId]);
      
      // Optimistically remove the item
      queryClient.setQueryData(['modern-cart', cartId], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          items: old.items.filter((item: ModernCartItem) => item.id !== itemId)
        };
      });
      
      return { previousCart };
    },
    onSuccess: () => {
      // Invalidate and refetch to get accurate server data
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
      
      toast({
        title: 'Removed from Cart',
        description: 'Item successfully removed from your cart',
      });
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousCart && cartId) {
        queryClient.setQueryData(['modern-cart', cartId], context.previousCart);
      }
      
      logger.error('[MODERN-CART] Remove item failed:', error);
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Failed to remove item from cart',
        variant: 'destructive',
      });
    }
  });

  // Update quantity mutation with optimistic updates
  const updateQuantityMutation = useMutation({
    mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
      if (!cartId) throw new Error('Cart not initialized');
      await unifiedCartManager.updateQuantity(cartId, itemId, quantity);
    },
    // 🔥 OPTIMISTIC UPDATES - Update UI immediately before server call
    onMutate: async ({ itemId, quantity }) => {
      if (!cartId) return;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['modern-cart', cartId] });
      
      // Snapshot the previous value for rollback
      const previousCart = queryClient.getQueryData(['modern-cart', cartId]);
      
      // Optimistically update the quantity
      queryClient.setQueryData(['modern-cart', cartId], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          items: old.items.map((item: ModernCartItem) => 
            item.id === itemId ? { ...item, quantity } : item
          )
        };
      });
      
      return { previousCart };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousCart && cartId) {
        queryClient.setQueryData(['modern-cart', cartId], context.previousCart);
      }
      
      logger.error('[MODERN-CART] Update quantity failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update quantity',
        variant: 'destructive',
      });
    }
  });

  // Clear cart mutation
  const clearCartMutation = useMutation({
    mutationFn: async () => {
      if (!cartId) throw new Error('Cart not initialized');
      // Clear by removing all items
      const items = cartData?.items || [];
      for (const item of items) {
        await unifiedCartManager.removeItem(cartId, item.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
      toast({
        title: 'Cart Cleared',
        description: 'All items removed from cart',
      });
    },
    onError: (error) => {
      logger.error('[MODERN-CART] Clear cart failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to clear cart',
        variant: 'destructive',
      });
    }
  });

  // Apply discount mutation
  const applyDiscountMutation = useMutation({
    mutationFn: async ({ code, context }: { code: string; context: DiscountContext }) => {
      if (!cartId) throw new Error('Cart not initialized');
      return await unifiedCartManager.applyDiscount(cartId, code, context);
    },
    onSuccess: (discount) => {
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
      toast({
        title: 'Discount Applied',
        description: `${discount.discount_amount}% discount applied successfully`,
      });
    },
    onError: (error) => {
      logger.error('[MODERN-CART] Apply discount failed:', error);
      toast({
        title: 'Discount Error',
        description: error instanceof Error ? error.message : 'Failed to apply discount',
        variant: 'destructive',
      });
    }
  });

  // Apply addon service mutation
  const applyAddonMutation = useMutation({
    mutationFn: async ({ serviceKey, amount }: { serviceKey: string; amount: number }) => {
      if (!cartId) throw new Error('Cart not initialized');
      await unifiedCartManager.applyAddonService(cartId, serviceKey, amount);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
      toast({
        title: 'Service Added',
        description: 'Add-on service applied to cart',
      });
    },
    onError: (error) => {
      logger.error('[MODERN-CART] Apply addon failed:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to apply add-on service',
        variant: 'destructive',
      });
    }
  });

  // 🔧 FIX: Split memoization to prevent infinite re-renders
  // Stable items array - only changes when actual cart data changes  
  const items = useMemo(() => cartData?.items || [], [cartData?.items]);
  
  // Stable totals - only changes when actual totals change
  const totals = useMemo(() => cartData?.totals || null, [cartData?.totals]);
  
  // Dynamic state that can change frequently without affecting items
  const dynamicState = useMemo(() => ({
    isLoading: isLoading || addItemMutation.isPending || removeItemMutation.isPending,
    isInitialized: !!cartId,
    syncStatus: queryError ? 'error' : isLoading ? 'syncing' : 'synced',
    error: error || (queryError instanceof Error ? queryError.message : null),
  }), [isLoading, cartId, queryError, error, addItemMutation.isPending, removeItemMutation.isPending]);

  // Combine stable and dynamic state
  const state = useMemo((): ModernCartState => ({
    items,
    totals,
    ...dynamicState,
  }), [items, totals, dynamicState]);

  // Memoized actions
  const actions = useMemo((): ModernCartActions => ({
    addItem: addItemMutation.mutateAsync,
    removeItem: removeItemMutation.mutateAsync,
    updateQuantity: (itemId: string, quantity: number) => 
      updateQuantityMutation.mutateAsync({ itemId, quantity }),
    clearCart: clearCartMutation.mutateAsync,
    applyDiscount: (code: string, context: DiscountContext) =>
      applyDiscountMutation.mutateAsync({ code, context }),
    removeDiscount: async (discountId: string) => {
      if (!cartId) throw new Error('Cart not initialized');
      await unifiedCartManager.removeDiscount(cartId, discountId);
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
    },
    applyAddonService: (serviceKey: string, amount: number) =>
      applyAddonMutation.mutateAsync({ serviceKey, amount }),
    removeAddonService: async (serviceKey: string) => {
      if (!cartId) throw new Error('Cart not initialized');
      await unifiedCartManager.removeAddonService(cartId, serviceKey);
      queryClient.invalidateQueries({ queryKey: ['modern-cart', cartId] });
    },
    getRecommendedAddons: async (context: { country: string; customerTier?: string }) => {
      if (!cartId) throw new Error('Cart not initialized');
      return await unifiedCartManager.getRecommendedAddons(cartId, context);
    },
    refresh: async () => {
      await refetch();
    },
  }), [
    cartId,
    addItemMutation.mutateAsync,
    removeItemMutation.mutateAsync,
    updateQuantityMutation.mutateAsync,
    clearCartMutation.mutateAsync,
    applyDiscountMutation.mutateAsync,
    applyAddonMutation.mutateAsync,
    queryClient,
    refetch
  ]);

  return { ...state, ...actions };
}

// Compatibility hooks for existing components
export const useModernCartItems = () => {
  const { items } = useModernCart();
  return items;
};

export const useModernCartCount = () => {
  const { items } = useModernCart();
  return items.length;
};

export const useModernCartTotals = () => {
  const { totals } = useModernCart();
  return totals;
};

export const useModernCartLoading = () => {
  const { isLoading } = useModernCart();
  return isLoading;
};